import { z } from 'zod';

/**
 * Helper function to create a string parameter with optional description.
 * This simplifies common Zod string definitions while maintaining LLM-friendly descriptions.
 * 
 * @param description - Optional description for LLM understanding
 * @returns Zod string schema with optional description
 * 
 * @example
 * ```typescript
 * @Tool({
 *   name: "greet",
 *   description: "Greets a person",
 *   input: z.object({
 *     name: stringParam("The name of the person to greet"),
 *     greeting: stringParam("The greeting message to use")
 *   })
 * })
 * ```
 */
export function stringParam(description?: string): z.ZodString {
  return description ? z.string().describe(description) : z.string();
}

/**
 * Helper function to create a number parameter with optional description.
 * This simplifies common Zod number definitions while maintaining LLM-friendly descriptions.
 * 
 * @param description - Optional description for LLM understanding
 * @returns Zod number schema with optional description
 * 
 * @example
 * ```typescript
 * @Tool({
 *   name: "calculate",
 *   description: "Performs a calculation",
 *   input: z.object({
 *     a: numberParam("First number"),
 *     b: numberParam("Second number")
 *   })
 * })
 * ```
 */
export function numberParam(description?: string): z.ZodNumber {
  return description ? z.number().describe(description) : z.number();
}

/**
 * Helper function to create a boolean parameter with optional description.
 * This simplifies common Zod boolean definitions while maintaining LLM-friendly descriptions.
 * 
 * @param description - Optional description for LLM understanding
 * @returns Zod boolean schema with optional description
 * 
 * @example
 * ```typescript
 * @Tool({
 *   name: "toggle",
 *   description: "Toggles a setting",
 *   input: z.object({
 *     enabled: booleanParam("Whether the feature is enabled")
 *   })
 * })
 * ```
 */
export function booleanParam(description?: string): z.ZodBoolean {
  return description ? z.boolean().describe(description) : z.boolean();
}

/**
 * Helper function to create an enum parameter with optional description.
 * This simplifies common Zod enum definitions while maintaining LLM-friendly descriptions.
 * 
 * @param values - Array of valid enum values
 * @param description - Optional description for LLM understanding
 * @returns Zod enum schema with optional description
 * 
 * @example
 * ```typescript
 * @Tool({
 *   name: "convert",
 *   description: "Converts between units",
 *   input: z.object({
 *     unit: enumParam(["celsius", "fahrenheit"], "Temperature unit to convert to")
 *   })
 * })
 * ```
 */
export function enumParam<T extends string>(
  values: T[], 
  description?: string
): z.ZodEnum<[T, ...T[]]> {
  const enumSchema = z.enum(values as [T, ...T[]]);
  return description ? enumSchema.describe(description) : enumSchema;
}

/**
 * Helper function to create an array parameter with optional description.
 * This simplifies common Zod array definitions while maintaining LLM-friendly descriptions.
 * 
 * @param itemSchema - The schema for array items
 * @param description - Optional description for LLM understanding
 * @returns Zod array schema with optional description
 * 
 * @example
 * ```typescript
 * @Tool({
 *   name: "sum",
 *   description: "Sums an array of numbers",
 *   input: z.object({
 *     numbers: arrayParam(numberParam(), "Array of numbers to sum")
 *   })
 * })
 * ```
 */
export function arrayParam<T extends z.ZodTypeAny>(
  itemSchema: T, 
  description?: string
): z.ZodArray<T> {
  const arraySchema = z.array(itemSchema);
  return description ? arraySchema.describe(description) : arraySchema;
}

/**
 * Helper function to create an optional parameter with optional description.
 * This simplifies common Zod optional definitions while maintaining LLM-friendly descriptions.
 * 
 * @param schema - The base schema to make optional
 * @param description - Optional description for LLM understanding
 * @returns Zod optional schema with optional description
 * 
 * @example
 * ```typescript
 * @Tool({
 *   name: "search",
 *   description: "Searches for items",
 *   input: z.object({
 *     query: stringParam("Search query"),
 *     limit: optionalParam(numberParam(), "Maximum number of results")
 *   })
 * })
 * ```
 */
export function optionalParam<T extends z.ZodTypeAny>(
  schema: T, 
  description?: string
): z.ZodOptional<T> {
  const optionalSchema = schema.optional();
  return description ? optionalSchema.describe(description) : optionalSchema;
}

/**
 * Helper function to create a union parameter with optional description.
 * This simplifies common Zod union definitions while maintaining LLM-friendly descriptions.
 * 
 * @param schemas - Array of possible schemas
 * @param description - Optional description for LLM understanding
 * @returns Zod union schema with optional description
 * 
 * @example
 * ```typescript
 * @Tool({
 *   name: "process",
 *   description: "Processes different types of data",
 *   input: z.object({
 *     data: unionParam([
 *       stringParam("Text data"),
 *       numberParam("Numeric data")
 *     ], "Data to process")
 *   })
 * })
 * ```
 */
export function unionParam(
  schemas: z.ZodTypeAny[], 
  description?: string
): z.ZodTypeAny {
  if (schemas.length < 2) {
    throw new Error('Union parameter requires at least 2 schemas');
  }
  const unionSchema = z.union(schemas as any);
  return description ? unionSchema.describe(description) : unionSchema;
} 